<template>
    <div class="s2w-folder-tree-item">
        <el-tooltip
            effect="light"
            placement="top"
            popper-class="pt-ui-tooltip"
            :visible-arrow="false"
            :content="service.folderDisabledMsg({ workspace, folder })"
            :disabled="!service.folderDisabledMsg({ workspace, folder })"
        >
            <div
                class="s2w-folder-tree-item-folder-info"
                :style="paddingStyle"
                :class="{
                    'is-current': service.folderSelector.selectedFolder === folder,
                    'is-editing': folder.isEditing,
                    'is-last-saved': isLastSaved,
                    disabled: service.isFolderDisabled({ workspace, folder })
                }"
                :data-folder-id="folder.folder_id"
                @click="selectFolder"
            >
                <PtIcon
                    class="folder-expand-icon"
                    :class="{
                        'has-children': folder.children && folder.children.length
                    }"
                    style="font-size: 24px;"
                    :icon="folder.expand ? 'SolidDropdownOpen' : 'SolidDropdownRight'"
                    @click.stop="service.toggleFolderExpand({ workspace, folder })"
                />
                <PtIcon
                    class="folder-icon"
                    style="font-size: 24px;"
                    :icon="service.getFolderIcon({ workspace, folder })"
                />
                <div class="folder-name-container">
                    <div
                        v-if="folder.isEditing"
                        class="folder-name-editor"
                        @click.stop
                    >
                        <!-- eslint-disable vue/no-mutating-props -->
                        <input
                            ref="folderNameInputRef"
                            v-model="folder.folder_name"
                            :class="{
                                error: !service.isFolderNameValid({ workspace, folder })
                            }"
                            type="text"
                            maxlength="40"
                            :disabled="folder.isUpdating"
                            @keypress.enter="onKeypressEnter"
                            @blur="service.finishEditFolder({ workspace, folder, parentFolder })"
                        >
                    </div>
                    <template v-else>
                        <!-- eslint-disable vue/no-v-html -->
                        <div
                            class="folder-name"
                            :title="titleText"
                            @dblclick="service.editFolder({ workspace, folder })"
                            v-html="service.renderFolderName(folder)"
                        />
                        <div
                            :title="titleText"
                            class="folder-elements-count"
                        >
                            {{ countText }}
                        </div>
                    </template>
                </div>
                <div class="folder-item-action-pane">
                    <PtIcon
                        v-if="service.hasEditFolderPermission({ workspace, folder })"
                        class="folder-item-action-item"
                        style="font-size: 24px;"
                        icon="SolidEdit"
                        @click.stop="service.editFolder({ workspace, folder })"
                    />
                    <PtIcon
                        v-if="service.hasAddSubFolderPermission({ workspace, folder, level })"
                        class="folder-item-action-item"
                        style="font-size: 24px;"
                        icon="SolidFolderAdd"
                        @click.stop="service.addFolder({
                            workspace,
                            parentFolder: folder,
                        })"
                    />
                    <div class="folder-item-status-tag">
                        {{ $t(`BaseSaveToWorkspace.FolderSelector.lastFlag.${service.folderSelector.latestType}`) }}
                    </div>
                </div>
            </div>
        </el-tooltip>
        <div v-if="level <= service.folderSelector.folderLoopProtectionFlag && folder.expand && folder.children">
            <FolderTreeItem
                v-for="child in folder.children"
                :key="child.folder_id"
                :folder="child"
                :parent-folder="folder"
                :level="level + 1"
            />
        </div>
    </div>
</template>

<script>
import PtIcon from '@patsnap-biz/icon';
import get from 'lodash/get';
import '@patsnap-ui/icon/assets/solid/edit.svg';
import '@patsnap-ui/icon/assets/solid/FolderAdd.svg';
import '@patsnap-ui/icon/assets/solid/FolderClosed2.svg';
import '@patsnap-ui/icon/assets/solid/autoFolder.svg';
import '@patsnap-ui/icon/assets/solid/DropdownOpen.svg';
import '@patsnap-ui/icon/assets/solid/DropdownRight.svg';
import Tooltip from 'element-ui/lib/tooltip';
import { scrollIntoViewIfNeeded } from '../../utils';

export default {
    name: 'FolderTreeItem',
    components: {
        PtIcon,
        ElTooltip: Tooltip,
    },
    inject: ['service'],
    props: {
        level: {
            type: Number,
            required: true,
        },
        folder: {
            type: Object,
            required: true,
        },
        parentFolder: {
            type: Object,
            default: null,
        },
    },
    computed: {
        isLastSaved() {
            return this.folder.folder_id === this.service.folderSelector.latestFolderId;
        },
        workspace() {
            return this.service.folderSelector.selectedWorkspace;
        },
        countText() {
            const { folder, workspace, service } = this;
            return service.getFolderElementsCountString({ workspace, folder });
        },
        titleText() {
            const { folder, countText } = this;
            return `${folder.folder_name} ${countText}`;
        },
        paddingStyle() {
            return {
                paddingLeft: `${25 * this.level + 10 || 10}px`,
            };
        },
    },
    watch: {
        'folder.isEditing': {
            handler() {
                this.$nextTick(() => {
                    const folderNameInputRef = get(this, '$refs.folderNameInputRef');
                    if (folderNameInputRef) {
                        folderNameInputRef.focus();
                        folderNameInputRef.select();
                    }
                });
            },
            immediate: true,
        },
    },
    mounted() {
        setTimeout(() => {
            this.$nextTick(() => {
                const { folderSelector } = this.service;
                if (folderSelector.initFolderScroll || this.folder !== folderSelector.selectedFolder) {
                    return false;
                }
                folderSelector.initFolderScroll = true;
                scrollIntoViewIfNeeded(this.$el);
                return true;
            });
        }, 0);
    },
    methods: {
        onKeypressEnter() {
            const folderNameInputRef = get(this, '$refs.folderNameInputRef');
            if (folderNameInputRef) {
                folderNameInputRef.blur();
            }
        },
        selectFolder() {
            const { workspace, folder } = this;
            if (this.service.isFolderDisabled({ workspace, folder })) {
                return;
            }
            this.service.selectFolder(folder);
        },
    },
};
</script>
